/**
 * Documents upload panel
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import HttpStatus from 'http-status-codes';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
/**
 * Redux
 */
import { useSelector, useDispatch } from 'react-redux';
import { lookupsState } from '../../redux/lookupsSlice';
import { updateLeadFiles, leadState } from '../../redux/leadSlice';
import { setStepValidation, wizardState } from '../../redux/wizardSlice';
/**
 * Helpers
 */
import api from '../../helpers/api';
import { steps } from '../../helpers/const';
import { getToken } from '../../helpers/auth';
/**
 * Components
 */
import FileDropzone from '../../components/FileDropzone';
import File from '../../components/File';

const useStyles = makeStyles(() => ({
  infoWrapper: {
    textAlign: 'left',
    paddingTop: 40,
  },
  infoIcon: {
    marginRight: 8,
  },
  infoTitle: {
    fontWeight: 'bold',
  },
  infoText: {
    fontSize: 14,
  },
}));

/**
 * @param template: string - Dropzone Graphic template ('complete', 'smart')
 * @returns {JSX.Element}
 */
function DocumentsUpload({ template }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { attachmentTypes } = useSelector(lookupsState);
  const { leadId } = useSelector(leadState);
  const { step } = useSelector(wizardState);
  const leadFiles = useSelector(leadState).files;
  const quoteFiles = useSelector(leadState).quote.files;
  const dispatch = useDispatch();

  const [files, setFiles] = useState([]);
  const [attachmentType, setAttachmentType] = useState({});

  const removeFileFromState = (file) => {
    setFiles((prevData) => {
      const data = _.cloneDeep(prevData);
      for (let i = data.length - 1; i >= 0; i -= 1) {
        if (data[i].key === file.key) {
          data.splice(i, 1);
        }
      }
      return data;
    });
  };

  const removeFile = async (file) => {
    const res = await api.delete({
      entryPoint: `/lead/${leadId}/attachment/${file.id}`,
      headers: { 'ga-token': getToken(leadId) },
    });
    if (res.status === HttpStatus.OK) {
      dispatch(updateLeadFiles({
        action: 'REMOVE',
        step,
        files: { id: file.id },
      }));
      removeFileFromState(file);
    }
  };

  const uploadFiles = async (filesToUpload, attachmentTypeName) => {
    for (let i = 0; i < filesToUpload.length; i += 1) {
      const record = filesToUpload[i];
      const formData = new FormData();
      formData.append('file', record);
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await api.multipart({
          entryPoint: `/lead/${leadId}/attachment/${attachmentTypeName}`,
          silentFail: true,
          data: formData,
          headers: { 'ga-token': getToken(leadId) },
        });
        if (res.status === HttpStatus.OK) {
          setFiles((prevData) => {
            const correctData = [...prevData];
            for (let y = 0; y < correctData.length; y += 1) {
              if (correctData[y].key === record.key) {
                correctData[y].id = res.data.attachment_id;
                correctData[y].key = res.data.attachment_id;
                correctData[y].loading = false;
              }
            }
            return correctData;
          });
          dispatch(updateLeadFiles({
            action: 'ADD',
            step,
            files: {
              id: res.data.attachment_id,
              key: res.data.attachment_id,
              loading: false,
              name: record.name,
            },
          }));
        } else {
          removeFileFromState(record);
        }
      } catch (e) {
        removeFileFromState(record);
      }
    }
  };

  useEffect(() => {
    const typeName = step === steps.trade ? 'image' : 'document';
    for (let i = 0; i < attachmentTypes.length; i += 1) {
      if (attachmentTypes[i].name === typeName) {
        setAttachmentType(attachmentTypes[i]);
      }
    }
  }, [attachmentTypes]);

  useEffect(() => {
    if (step === steps.trade) {
      setFiles([...quoteFiles]);
      return;
    }
    setFiles([...leadFiles]);
    dispatch(setStepValidation(true));
  }, []);

  const filesMemo = useMemo(() => (
    files.map((file) => (
      <File
        key={file.key}
        file={file}
        onRemove={removeFile}
      />
    ))
  ), [JSON.stringify(files)]);

  return (
    <div className="col-xs-12">
      <FileDropzone
        onUploadFiles={(data, attachmentTypeName) => {
          setFiles((prevState) => [...prevState, ...data]);
          uploadFiles(data, attachmentTypeName);
        }}
        maxSize={step === steps.trade ? 2000000 : 5000000}
        acceptedFileTypes={attachmentType.valid_mimetypes}
        attachmentTypeName={attachmentType.name}
        template={template}
      />
      <div className="d-flex flex-wrap">
        { filesMemo }
      </div>
      { step === steps.documents && (
        <div className={classes.infoWrapper}>
          <div className="d-flex">
            <CreditCardIcon color="primary" className={classes.infoIcon} />
            <div>
              <div className={classes.infoTitle}>
                { t('Front and back identification document') }
              </div>
              <div className={classes.infoText}>
                { t('Upload your valid identity card, passport or driving license') }
              </div>
            </div>
          </div>
          <div className="d-flex mt-3">
            <ContactEmergencyIcon color="primary" className={classes.infoIcon} />
            <div>
              <div className={classes.infoTitle}>
                { t('Fiscal Code front and back') }
              </div>
              <div className={classes.infoText}>
                { t('Upload your health card or alternatively your valid tax code') }
              </div>
            </div>
          </div>
          <div className="d-flex mt-3">
            <LocalAtmIcon color="primary" className={classes.infoIcon} />
            <div>
              <div className={classes.infoTitle}>
                { t('Pay slip or tax return') }
              </div>
              <div className={classes.infoText}>
                { t('If you want to finance your vehicle, upload your last two payslips or your last tax return if you are self-employed') }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
DocumentsUpload.propTypes = {
  template: PropTypes.string,
};

DocumentsUpload.defaultProps = {
  template: 'complete',
};
export default DocumentsUpload;
